import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import './MemeCoinBots.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

export default function MemeCoinBots() {
  const [config, setConfig] = useState(null);
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    walletPubkey: '',
    name: '',
    mode: 'moderate',
    authMode: 'manual',
    categories: ['new', 'soon'],
    selectionMode: 'auto',
    targetMint: '',
    sessionDurationMinutes: 60,
    dailyLossLimitMode: 'off',
    dailyLossLimitValue: '',
    rugGuardEnabled: true,
  });

  const loadAll = async () => {
    setError(null);
    try {
      const [c, b] = await Promise.all([
        axios.get(`${API_BASE}/meme-bots/config`),
        axios.get(`${API_BASE}/meme-bots/my-bots`, { headers: authHeaders() }),
      ]);
      setConfig(c.data);
      setBots(b.data.bots || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const createBot = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await axios.post(
        `${API_BASE}/meme-bots/my-bots`,
        {
          walletPubkey: form.walletPubkey.trim(),
          name: form.name.trim() || undefined,
          mode: form.mode,
          authMode: form.authMode,
          categories: form.categories,
          selectionMode: form.selectionMode,
          targetMint: form.selectionMode === 'manual' ? form.targetMint.trim() : null,
          sessionDurationMinutes: Number(form.sessionDurationMinutes) || 60,
          dailyLossLimitMode: form.dailyLossLimitMode,
          dailyLossLimitValue:
            form.dailyLossLimitMode === 'off' ? null : parseFloat(form.dailyLossLimitValue) || 0,
          rugGuardEnabled: form.rugGuardEnabled,
        },
        { headers: authHeaders() }
      );
      await loadAll();
      alert('Bot created.');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API_BASE}/meme-bots/my-bots/${id}`, { status }, { headers: authHeaders() });
      await loadAll();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
  };

  const removeBot = async (id) => {
    if (!window.confirm('Delete this bot?')) return;
    try {
      await axios.delete(`${API_BASE}/meme-bots/my-bots/${id}`, { headers: authHeaders() });
      await loadAll();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
  };

  const toggleCategory = (cat) => {
    setForm((f) => {
      const has = f.categories.includes(cat);
      return {
        ...f,
        categories: has ? f.categories.filter((c) => c !== cat) : [...f.categories, cat],
      };
    });
  };

  if (loading) return <div className="meme-bots-page loading">Loading…</div>;

  const modes = config?.modes || {};

  return (
    <div className="meme-bots-page">
      <header className="meme-bots-header">
        <p>
          <Link to="/dashboard">← Dashboard</Link>
        </p>
        <h1>Meme coin bots</h1>
        <p className="subtitle">
          Connect Phantom on the{' '}
          <Link to="/memecoins">Meme Coins</Link> page to trade manually. Here you configure bots (manual or timed session with a funded hot
          wallet). Shared quotes refresh about every {config?.tickIntervalMs / 1000 || 2}s server-side.
        </p>
      </header>

      {error && <div className="error-banner">{error}</div>}

      <section className="card">
        <h2>Mode presets (USDC limits)</h2>
        <div className="mode-grid">
          {Object.entries(modes).map(([k, v]) => (
            <div key={k} className="mode-card">
              <h3>{k}</h3>
              <p>Slippage: {v.slippageBps != null ? `${v.slippageBps / 100}%` : '—'}</p>
              <p>Max trade: {v.maxTradeUsdc != null ? `$${v.maxTradeUsdc} USDC` : '—'}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="card">
        <h2>Create bot</h2>
        <form onSubmit={createBot} className="bot-form">
          <div className="form-row">
            <label>Solana wallet (Phantom) public key</label>
            <input
              required
              value={form.walletPubkey}
              onChange={(e) => setForm({ ...form, walletPubkey: e.target.value })}
              placeholder="Your wallet address"
            />
          </div>
          <div className="form-row">
            <label>Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Optional" />
          </div>
          <div className="form-row two">
            <div>
              <label>Risk mode</label>
              <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
                <option value="conservative">Conservative</option>
                <option value="moderate">Moderate</option>
                <option value="aggressive">Aggressive</option>
              </select>
            </div>
            <div>
              <label>Auth</label>
              <select value={form.authMode} onChange={(e) => setForm({ ...form, authMode: e.target.value })}>
                <option value="manual">Manual (approve each swap in Phantom)</option>
                <option value="session">Session (auto-sign via hot wallet — see API docs)</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <label>Categories (Alpha Scan: new / soon / bonded / trending)</label>
            <div className="chips">
              {['new', 'soon', 'bonded', 'trending'].map((cat) => (
                <button
                  key={cat}
                  type="button"
                  className={form.categories.includes(cat) ? 'chip on' : 'chip'}
                  onClick={() => toggleCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
          <div className="form-row two">
            <div>
              <label>Coin selection</label>
              <select
                value={form.selectionMode}
                onChange={(e) => setForm({ ...form, selectionMode: e.target.value })}
              >
                <option value="auto">Auto (from categories)</option>
                <option value="manual">Manual mint</option>
              </select>
            </div>
            {form.selectionMode === 'manual' && (
              <div>
                <label>Target mint</label>
                <input
                  required
                  value={form.targetMint}
                  onChange={(e) => setForm({ ...form, targetMint: e.target.value })}
                  placeholder="Token mint address"
                />
              </div>
            )}
          </div>
          <div className="form-row two">
            <div>
              <label>Session duration (minutes, session auth)</label>
              <input
                type="number"
                min={5}
                value={form.sessionDurationMinutes}
                onChange={(e) => setForm({ ...form, sessionDurationMinutes: e.target.value })}
              />
            </div>
            <div>
              <label>Daily loss limit</label>
              <select
                value={form.dailyLossLimitMode}
                onChange={(e) => setForm({ ...form, dailyLossLimitMode: e.target.value })}
              >
                <option value="off">Off</option>
                <option value="usdc">USDC amount</option>
                <option value="percent">Percent of day</option>
                <option value="auto_percent">Auto (platform %)</option>
              </select>
            </div>
          </div>
          {form.dailyLossLimitMode !== 'off' && (
            <div className="form-row">
              <label>Value</label>
              <input
                value={form.dailyLossLimitValue}
                onChange={(e) => setForm({ ...form, dailyLossLimitValue: e.target.value })}
                placeholder="e.g. 100 or 5"
              />
            </div>
          )}
          <div className="form-row">
            <label>
              <input
                type="checkbox"
                checked={form.rugGuardEnabled}
                onChange={(e) => setForm({ ...form, rugGuardEnabled: e.target.checked })}
              />{' '}
              Rug / anomaly guard (thresholds from server)
            </label>
          </div>
          <button type="submit" className="primary-btn">
            Create bot
          </button>
        </form>
      </section>

      <section className="card">
        <h2>Your bots ({bots.length} / max {config?.maxBotsPerWallet ?? 5})</h2>
        {bots.length === 0 ? (
          <p>No bots yet.</p>
        ) : (
          <ul className="bot-list">
            {bots.map((b) => (
              <li key={b.id} className="bot-item">
                <div>
                  <strong>{b.name || `Bot #${b.id}`}</strong>{' '}
                  <span className="badge">{b.mode}</span> <span className="badge">{b.auth_mode}</span>{' '}
                  <span className={`status ${b.status}`}>{b.status}</span>
                  <div className="meta">
                    Wallet: {b.wallet_pubkey?.slice(0, 4)}…{b.wallet_pubkey?.slice(-4)}{' '}
                    · {b.selection_mode === 'manual' ? `mint ${b.target_mint?.slice(0, 8)}…` : 'auto'}
                  </div>
                </div>
                <div className="actions">
                  {b.status !== 'running' && (
                    <button type="button" className="btn-small primary-btn" onClick={() => setStatus(b.id, 'running')}>
                      Start
                    </button>
                  )}
                  {b.status === 'running' && (
                    <button type="button" className="btn-small" onClick={() => setStatus(b.id, 'stopped')}>
                      Stop
                    </button>
                  )}
                  <button type="button" className="btn-small btn-danger" onClick={() => removeBot(b.id)}>
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
